import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import useContext from '../../../../hooks/useContext';
import useHistory from '../../../../Router/useHistory';

export type GridView = 'grid-large' | 'grid-small';

type ImageType = 'contextual' | 'product';

type TViewSettingsContext = {
  productView: GridView;
  showContextualImage: boolean;

  enableContextualImage: () => void;
  disableContextualImage: () => void;
  enableLargeGrid: () => void;
  enableSmallGrid: () => void;
};

const ViewSettingsContext = createContext<TViewSettingsContext | undefined>(undefined);

interface IViewSettingsProvider {
  children: ReactNode;
  useContextualImageByDefault?: boolean;
}

interface LocationState {
  imageType?: ImageType;
  gridView?: GridView;
}

export const ViewSettingsProvider = ({
  children,
  useContextualImageByDefault,
}: IViewSettingsProvider) => {
  const { location, replace } = useHistory();

  const [productView, setProductView] = useState<GridView>('grid-small');
  const [showContextualImage, setShowContextualImage] = useState(!!useContextualImageByDefault);

  const locationState = (location?.state as LocationState) || {};

  const enableContextualImage = useCallback(() => {
    setShowContextualImage(true);
    replace(window.location.href, { ...locationState, imageType: 'contextual' });
  }, [locationState]);

  const disableContextualImage = useCallback(() => {
    setShowContextualImage(false);
    replace(window.location.href, { ...locationState, imageType: 'product' });
  }, [locationState]);

  const enableLargeGrid = useCallback(() => {
    setProductView('grid-large');
    replace(window.location.href, { ...locationState, gridView: 'grid-large' });
  }, [locationState]);

  const enableSmallGrid = useCallback(() => {
    setProductView('grid-small');
    replace(window.location.href, { ...locationState, gridView: 'grid-small' });
  }, [locationState]);

  useEffect(() => {
    const prevImageType = locationState?.imageType as ImageType | undefined;

    if (prevImageType === 'contextual') {
      setShowContextualImage(true);
    } else if (prevImageType === 'product') {
      setShowContextualImage(false);
    } else {
      setShowContextualImage(!!useContextualImageByDefault);
    }
  }, [locationState, useContextualImageByDefault]);

  useEffect(() => {
    const prevGridView = locationState?.gridView as GridView | undefined;

    if (prevGridView) {
      setProductView(prevGridView);
    } else {
      setProductView('grid-small');
    }
  }, [locationState]);

  const value = useMemo(
    () => ({
      productView,
      showContextualImage,
      enableContextualImage,
      disableContextualImage,
      enableLargeGrid,
      enableSmallGrid,
    }),
    [productView, showContextualImage],
  );

  return <ViewSettingsContext.Provider value={value}>{children}</ViewSettingsContext.Provider>;
};

const useViewSettings = () => useContext(ViewSettingsContext);

export default useViewSettings;
